<template>
  <Modal
    :modal-active="modelValue"
    has-header-border
    has-footer-border
    max-width="max-w-lg"
    @close="close"
  >
    <template #header>
      <h2 class="text-xl font-bold text-textPrimary text-left">Exportar evidências</h2>
    </template>

    <div class="flex flex-col gap-6 text-left">
      <div class="flex flex-col gap-2">
        <p class="text-sm font-bold text-textPrimary">Formato de exportação</p>
        <div class="flex gap-3">
          <label
            v-for="opt in FORMAT_OPTIONS"
            :key="opt.value"
            class="flex-1 flex flex-col gap-1 cursor-pointer rounded-lg border-2 p-3 transition-colors"
            :class="
              format === opt.value
                ? 'border-primary bg-primary/5'
                : 'border-gray-200 hover:border-gray-300'
            "
            :data-testid="`export-format-${opt.value}`"
          >
            <div class="flex items-center gap-2">
              <input v-model="format" type="radio" :value="opt.value" class="accent-primary" />
              <span class="text-sm font-medium text-textPrimary">{{ opt.label }}</span>
            </div>
            <span class="text-xs text-neutral50 pl-6">{{ opt.description }}</span>
          </label>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <div class="flex items-center gap-1.5">
          <svg class="w-4 h-4 text-textPrimary" viewBox="0 0 24 24" aria-hidden="true">
            <path :d="mdiFilterOutline" fill="currentColor" />
          </svg>
          <p class="text-sm font-bold text-textPrimary">Filtros</p>
        </div>
        <p v-if="filterTags.length === 0" class="text-sm text-neutral50">
          Nenhum filtro ativo: a exportação leva a fila inteira.
        </p>
        <div v-else class="flex flex-wrap gap-2">
          <span
            v-for="tag in filterTags"
            :key="tag.id"
            class="inline-flex items-center rounded-full border border-neutral30 px-3 py-1 text-xs text-textPrimary"
          >
            {{ tag.label }}
          </span>
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-bold text-textPrimary">
          Tipo de data <span class="text-negative" aria-hidden="true">*</span>
        </label>
        <select
          v-model="dateType"
          class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-textPrimary bg-white focus:outline-none focus:ring-2 focus:ring-primary30"
          data-testid="export-date-type"
        >
          <option value="" disabled>Selecione</option>
          <option value="plantao">Data do plantão</option>
          <option value="upload">Data do upload</option>
        </select>
      </div>

      <div class="flex gap-3">
        <div class="flex-1 flex flex-col gap-1">
          <label class="text-sm font-bold text-textPrimary">
            Data início <span class="text-negative" aria-hidden="true">*</span>
          </label>
          <input
            v-model="startDate"
            type="date"
            class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-textPrimary focus:outline-none focus:ring-2 focus:ring-primary30"
            data-testid="export-start-date"
          />
        </div>
        <div class="flex-1 flex flex-col gap-1">
          <label class="text-sm font-bold text-textPrimary">
            Data fim <span class="text-negative" aria-hidden="true">*</span>
          </label>
          <input
            v-model="endDate"
            type="date"
            class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-textPrimary focus:outline-none focus:ring-2 focus:ring-primary30"
            :class="{ 'border-negative': isDateRangeInverted }"
            data-testid="export-end-date"
          />
          <p
            v-if="isDateRangeInverted"
            class="text-xs text-negative"
            data-testid="export-date-error"
          >
            A data fim não pode ser anterior à data início.
          </p>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex gap-4 w-full">
        <div class="flex-1">
          <OutlineButton
            text="Cancelar"
            width="100%"
            height="48px"
            :disabled="exporting"
            data-testid="export-cancel-button"
            @click="close"
          />
        </div>
        <div class="flex-1">
          <FilledButton
            text="Exportar"
            width="100%"
            height="48px"
            :disabled="!canExport"
            :loading="exporting"
            data-testid="export-confirm-button"
            @click="handleExport"
          />
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { mdiFilterOutline } from '@mdi/js'

import Modal from '@/components/designSystem/modals/Modal.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'

import type { CheckValidationFilterTag } from '../filterTags'

export type ExportDateType = 'plantao' | 'upload'

export interface ExportPayload {
  format: 'zip' | 'pdf'
  dateType: ExportDateType
  startDate: string
  endDate: string
}

const props = defineProps<{
  modelValue: boolean
  exporting: boolean
  filterTags: CheckValidationFilterTag[]
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  export: [payload: ExportPayload]
}>()

const FORMAT_OPTIONS = [
  {
    label: 'Arquivos separados (.zip)',
    value: 'zip' as const,
    description: 'Um arquivo por evidência, compactados em .zip'
  },
  {
    label: 'PDF único compilado',
    value: 'pdf' as const,
    description:
      'Todas as evidências em um só documento (com página de resumo), na ordem da data do plantão'
  }
]

const format = ref<'zip' | 'pdf'>('zip')
const dateType = ref<ExportDateType | ''>('')
const startDate = ref('')
const endDate = ref('')

const isDateRangeInverted = computed(
  () => !!startDate.value && !!endDate.value && endDate.value < startDate.value
)

const canExport = computed(
  () =>
    !props.exporting &&
    !!dateType.value &&
    !!startDate.value &&
    !!endDate.value &&
    !isDateRangeInverted.value
)

// Reseta os campos ao abrir o modal.
watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      format.value = 'zip'
      dateType.value = ''
      startDate.value = ''
      endDate.value = ''
    }
  }
)

function close() {
  emit('update:modelValue', false)
}

function handleExport() {
  if (!canExport.value) return

  emit('export', {
    format: format.value,
    dateType: dateType.value as ExportDateType,
    startDate: startDate.value,
    endDate: endDate.value
  })
}
</script>
